import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { ActionIcon, createStyles, Group, Stack, Tooltip } from '@mantine/core'
import React, { ReactElement, ReactNode, ReactNodeArray } from 'react'

import { AddressSearch } from '../../AddressSearch'
import {
  TAddressSearchItem,
  TAddressSearchOption,
} from '../../AddressSearch/types'
import { MapSettings } from '../MapSettings'
import { useMapSettings } from '../MapSettings/useMapSettings'
import { useMap } from '../useMap'

type TMapControlsProps = {
  controls?: ReactNode[]
  useAddressSearchOptions?: (opts: {
    searchValue: string
  }) => TAddressSearchOption[]
  showAddressSearch?: boolean
  showCenterElements?: boolean
  extraSettingsForms?: ReactNode | ReactNodeArray
  addressSearchPlaceholder?: string
  onAddressSelected?: (address: TAddressSearchItem) => void
  bottomElement?: ReactNode
  viewControls?: ReactNode[]
}

const useStyle = createStyles((theme) => ({
  root: {
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing.md,
    alignItems: 'start',
  },
  bottomRightRoot: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    padding: theme.spacing.md,
  },
  bottomLeftRoot: {
    position: 'absolute',
    left: 0,
    bottom: 0,
    right: 50,
    padding: theme.spacing.md,
  },
  control: {
    boxShadow: theme.shadows.md,
  },
  addressSearch: {
    marginRight: theme.spacing.md,
    flex: '1',
    maxWidth: '700px',
  },
}))

export const MapControls = (props: TMapControlsProps): ReactElement => {
  const {
    controls = [],
    useAddressSearchOptions,
    showAddressSearch,
    showCenterElements,
    extraSettingsForms,
    addressSearchPlaceholder,
    onAddressSelected,
    bottomElement,
    viewControls,
  } = props

  const { cx, classes } = useStyle()

  const { settings } = useMapSettings()

  const { jumpTo, zoom, map, centerAllElements } = useMap()

  const onSelectAddress = (address: TAddressSearchItem): void => {
    if (!address?.lonlat) return
    jumpTo(address.lonlat, 16)
    if (onAddressSelected) {
      onAddressSelected(address)
    }
  }

  const zoomIn = (): void => {
    const currentZoom = Math.round(map.getView().getZoom() ?? 9)
    zoom(currentZoom + 1)
  }

  const zoomOut = (): void => {
    const currentZoom = Math.round(map.getView().getZoom() ?? 9)
    zoom(currentZoom - 1)
  }

  const addressSearchComponent = (
    <AddressSearch
      onAddressSelected={onSelectAddress}
      useAddressSearchOptions={useAddressSearchOptions}
      lonlat={settings.center}
      placeholder={addressSearchPlaceholder}
    />
  )

  return (
    <>
      <div className={classes.root}>
        {showAddressSearch ? (
          <div className={cx(classes.control, classes.addressSearch)}>
            {addressSearchComponent}
          </div>
        ) : (
          <div
            className={classes.addressSearch}
            style={{ pointerEvents: 'none' }}
          />
        )}
        <div style={{ pointerEvents: 'none' }}></div>
        <div>
          <Group spacing="sm" noWrap>
            {[
              ...controls,
              <MapSettings key="map" extraSettingsForms={extraSettingsForms} />,
            ].map((control, index) => (
              <div key={index} className={classes.control}>
                {control}
              </div>
            ))}
          </Group>
        </div>
      </div>
      <div className={classes.bottomRightRoot}>
        <Stack>
          {viewControls &&
            viewControls.map((control, index) => (
              <div key={index} className={classes.control}>
                {control}
              </div>
            ))}
          {showCenterElements && (
            <div className={classes.control}>
              <Tooltip
                label={i18n.t(
                  `centre_view_on_all_elements`,
                  `Ansicht auf alle Elemente zentrieren`,
                )}
                // position="left"
                withArrow
                openDelay={500}
                withinPortal
              >
                <ActionIcon variant="light" onClick={() => centerAllElements()}>
                  <FontAwesomeIcon icon="arrows-to-circle" />
                </ActionIcon>
              </Tooltip>
            </div>
          )}
          <Stack spacing={5}>
            <div className={classes.control}>
              <ActionIcon variant="light" onClick={zoomIn}>
                <FontAwesomeIcon icon="plus" />
              </ActionIcon>
            </div>
            <div className={classes.control}>
              <ActionIcon variant="light" onClick={zoomOut}>
                <FontAwesomeIcon icon="minus" />
              </ActionIcon>
            </div>
          </Stack>
        </Stack>
      </div>
      {bottomElement && (
        <div className={classes.bottomLeftRoot}>{bottomElement}</div>
      )}
    </>
  )
}
